import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { useIsAdmin } from "../../lib/permissions";
import { holdReason } from "../../lib/tasks";
import type { IssueDetail } from "../../types";
import { AddLoopsDialog } from "../tasks/loop-budget";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

/**
 * Shown in the Factory card when the work order is waiting_for_human: the
 * Verifier's questions, a reply box, and the two resume actions. Request
 * rework sends the findings plus every human reply to the Fixer; Continue
 * overrides the hold and moves the issue to business testing, so only admins see it.
 * A hold for a spent loop budget swaps Request rework for an admin's Add loops.
 */
export function HumanHoldBlock({
  issue,
  stationName,
  onChanged,
}: {
  issue: IssueDetail;
  stationName: string | undefined;
  onChanged: () => void;
}) {
  const taskQuery = useQuery({
    queryKey: ["task", issue.taskId],
    queryFn: () => api.task(issue.taskId!),
    enabled: Boolean(issue.taskId),
    refetchInterval: 10_000,
  });
  const queryClient = useQueryClient();
  const admin = useIsAdmin();
  const [reply, setReply] = useState("");
  const [replied, setReplied] = useState(false);
  const post = useMutation({
    mutationFn: () => api.addComment(issue.identifier, { body: reply.trim() }),
    onSuccess: () => {
      setReply("");
      setReplied(true);
      toast.success("Reply posted");
      void queryClient.invalidateQueries({ queryKey: ["issue-comments", issue.identifier] });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Comment failed"),
  });
  const resume = useMutation({
    mutationFn: (action: "rework" | "continue") => api.resumeIssue(issue.identifier, action),
    onSuccess: (_result, action) => {
      toast.success(action === "rework" ? "Sent to the Fixer" : "Continuing to business testing");
      void queryClient.invalidateQueries({ queryKey: ["task", issue.taskId] });
      onChanged();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Resume failed"),
  });

  const task = taskQuery.data?.task;
  if (task?.status !== "waiting_for_human") return null;
  // `currentRun` is the holding station's run in full: `/tasks/:id` resolves it
  // server-side (the task's own `currentRunId` is cleared for this status), so
  // the questions don't need every run's `structured` sent down just in case.
  const holdingRun = taskQuery.data?.currentRun;
  const questions = Array.isArray(holdingRun?.structured?.questions)
    ? (holdingRun.structured.questions as string[])
    : [];

  const reason = holdReason(task);
  const budget = taskQuery.data?.loopBudget;

  const rework = (
    <Button size="sm" className="flex-1" disabled={resume.isPending} onClick={() => resume.mutate("rework")}>
      Request rework
    </Button>
  );
  const continueAnyway = admin && (
    <Button
      size="sm"
      variant="outline"
      className="flex-1"
      disabled={resume.isPending}
      onClick={() => resume.mutate("continue")}
    >
      Continue anyway
    </Button>
  );

  return (
    <div className="space-y-2 rounded-lg border border-violet-500/30 bg-violet-500/5 px-2.5 py-2">
      <p className="text-xs font-medium text-violet-700 dark:text-violet-300">
        {reason?.kind === "loop_budget"
          ? `Loop budget spent — ${reason.used} of ${reason.allowed} Fixer loops used`
          : reason?.kind === "no_progress"
            ? "The Fixer pushed nothing — waiting on you"
            : `${stationName ?? "The reviewer"} requested changes — waiting on you`}
      </p>
      {questions.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
          {questions.map((question) => (
            <li key={question}>{question}</li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        {reason?.kind === "loop_budget" ? (
          admin ? (
            "The Verifier still wants changes. Add loops to send its findings and your replies back to the Fixer, or continue to business testing."
          ) : (
            "The Verifier still wants changes. Reply with guidance and ask an admin to add loops."
          )
        ) : reason?.kind === "no_progress" ? (
          <>
            The pull request head is still at{" "}
            <span className="font-mono text-foreground">{reason.headSha.slice(0, 7)}</span>, so the Verifier
            would inspect the same code again. Reply with guidance, then request rework to send it back to the
            Fixer.
          </>
        ) : (
          "Answer the questions with a reply, then request rework. The Fixer sees the findings and every comment left since the hold."
        )}
      </p>
      <div className="flex items-start gap-1.5">
        <Textarea
          value={reply}
          onChange={(event) => setReply(event.target.value)}
          placeholder={reason ? "Reply with guidance…" : "Reply to the reviewer…"}
          rows={2}
          className="min-h-12 text-xs"
        />
        <Button
          size="icon"
          variant="outline"
          aria-label="Post reply"
          disabled={!reply.trim() || post.isPending}
          onClick={() => post.mutate()}
        >
          <Send className="size-3.5" />
        </Button>
      </div>
      {reason?.kind === "loop_budget" ? (
        admin &&
        budget && (
          <div className="flex flex-wrap gap-1.5">
            <AddLoopsDialog
              budget={budget}
              target={{ taskId: task.id, issueRef: issue.identifier }}
              held
              onChanged={onChanged}
              trigger={
                <Button size="sm" className="flex-1">
                  Add loops and continue
                </Button>
              }
            />
            {continueAnyway}
          </div>
        )
      ) : (
        <div className="flex gap-1.5">
          {replied ? (
            rework
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" className="flex-1" disabled={resume.isPending}>
                  Request rework
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Request rework without a reply?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {reason?.kind === "no_progress"
                      ? "You have not replied here. The Fixer pushed nothing last time; without guidance it may do the same again."
                      : "You have not replied here. The Fixer will only see the Verifier's findings — if the Verifier asked questions, answer them first so the rework goes the right way."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Reply first</AlertDialogCancel>
                  <AlertDialogAction onClick={() => resume.mutate("rework")}>
                    Request rework anyway
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {continueAnyway}
        </div>
      )}
    </div>
  );
}
